// AGC: Returns true on real desktop browsers — never on Capacitor native, never on touch-only tablets.
// AGC: Used by MainMenu to switch between mobile shell (BottomNav) and desktop shell (sidebar grid).
// AGC: QA override: ?layout=desktop / ?layout=mobile in URL, or localStorage 'droidfarm.layout' = 'desktop'|'mobile'.
import { useEffect, useState } from 'react'
import { Capacitor } from '@capacitor/core'

// AGC: min-width: 1024px matches Tailwind lg breakpoint
// AGC: hover: hover and pointer: fine excludes touch-primary devices (tablets, phones) even at wide viewports
const DESKTOP_QUERY = '(min-width: 1024px) and (hover: hover) and (pointer: fine)'
const STORAGE_KEY = 'droidfarm.layout'

type Override = 'desktop' | 'mobile' | null

// AGC: Read override from URL (?layout=desktop|mobile) then localStorage fallback
const readOverride = (): Override => {
  if (typeof window === 'undefined') return null
  // AGC: URL takes precedence — easy to share / bookmark a forced layout
  const urlOverride = new URLSearchParams(window.location.search).get('layout')
  if (urlOverride === 'desktop' || urlOverride === 'mobile') {
    try { window.localStorage.setItem(STORAGE_KEY, urlOverride) } catch { /* AGC: ignore storage errors */ }
    return urlOverride
  }
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (stored === 'desktop' || stored === 'mobile') return stored
  } catch { /* AGC: ignore storage errors */ }
  return null
}

// AGC: Compute initial value synchronously to avoid flash on first render
const computeInitial = (): boolean => {
  if (typeof window === 'undefined') return false
  if (Capacitor.isNativePlatform()) return false  // AGC: Native iOS/Android — always mobile shell
  const override = readOverride()
  if (override) return override === 'desktop'
  return window.matchMedia(DESKTOP_QUERY).matches
}

// AGC: Hook — subscribes to matchMedia threshold crossings only; never listens when override is pinned
export const useIsDesktopLayout = (): boolean => {
  const [isDesktop, setIsDesktop] = useState(computeInitial)

  useEffect(() => {
    if (Capacitor.isNativePlatform()) return  // AGC: Native — never switch, never listen
    const override = readOverride()
    if (override) {
      // AGC: Override pinned — do not react to viewport changes at all
      setIsDesktop(override === 'desktop')
      return
    }
    const mql = window.matchMedia(DESKTOP_QUERY)
    const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches)
    mql.addEventListener('change', onChange)
    setIsDesktop(mql.matches)  // AGC: Sync once on mount in case query changed before listener attached
    return () => mql.removeEventListener('change', onChange)
  }, [])

  return isDesktop
}
